<template>
  <div class="booking-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">Bookings</p>
        <h1>Real appointments</h1>
        <p class="subtitle">
          Appointments created by your AI agent from live conversations.
        </p>
      </div>

      <div class="header-actions">
        <button class="ghost-btn" @click="loadPage" :disabled="loading">
          <span :class="{ spin: loading }">↻</span>
          Refresh
        </button>
        <router-link to="/app/booking/settings" class="settings-btn">
          Settings
        </router-link>
      </div>
    </header>

    <div v-if="error" class="error-box">
      <strong>Unable to load bookings</strong>
      <p>{{ error }}</p>
    </div>

    <section class="kpi-grid">
      <div class="kpi-card">
        <span>Today</span>
        <strong>{{ todayCount }}</strong>
      </div>
      <div class="kpi-card">
        <span>This week</span>
        <strong>{{ weekCount }}</strong>
      </div>
      <div class="kpi-card">
        <span>Confirmed</span>
        <strong>{{ confirmedRate }}%</strong>
      </div>
      <div class="kpi-card">
        <span>Total from backend</span>
        <strong>{{ bookings.length }}</strong>
      </div>
    </section>

    <div v-if="loading" class="loading-grid">
      <div class="skeleton calendar"></div>
      <div class="skeleton list"></div>
    </div>

    <div v-else class="content-grid">
      <section class="card calendar-card">
        <div class="card-header">
          <button class="icon-btn" @click="prevMonth" aria-label="Previous month">‹</button>
          <h2>{{ currentMonthLabel }}</h2>
          <button class="icon-btn" @click="nextMonth" aria-label="Next month">›</button>
        </div>

        <div class="calendar-grid">
          <div v-for="day in dayNames" :key="day" class="day-name">
            {{ day }}
          </div>

          <button
            v-for="day in calendarDays"
            :key="day.date"
            type="button"
            class="calendar-cell"
            :class="{ muted: !day.inMonth, today: day.isToday }"
            @click="selectDate(day.date)"
          >
            <span>{{ day.day }}</span>
            <div class="events">
              <span
                v-for="event in getEvents(day.date).slice(0, 3)"
                :key="event.id"
                class="event"
                :class="event.status"
              >
                {{ event.time }}
              </span>
            </div>
          </button>
        </div>
      </section>

      <aside class="card list-card">
        <div class="list-header">
          <div>
            <h2>Upcoming appointments</h2>
            <p>{{ activeAgentName }}</p>
          </div>
          <span class="pill">{{ upcomingBookings.length }}</span>
        </div>

        <div v-if="bookings.length === 0" class="empty-state">
          <h3>No bookings yet</h3>
          <p>
            When the AI collects date, time, service, name, and phone, real
            bookings will appear here.
          </p>
        </div>

        <div v-else class="booking-list">
          <article
            v-for="booking in upcomingBookings"
            :key="booking.id"
            class="booking-item"
            @click="openBooking(booking)"
          >
            <div>
              <strong>{{ booking.name || 'Unnamed client' }}</strong>
              <p>{{ formatDate(booking.date) }} · {{ booking.time }} · {{ booking.duration }} min</p>
              <small>{{ booking.service || booking.notes || 'No service captured' }}</small>
            </div>

            <div class="booking-actions" @click.stop>
              <span class="status" :class="booking.status">{{ statusLabel(booking.status) }}</span>
              <select
                :value="booking.status"
                :disabled="savingId === booking.id"
                @change="updateStatus(booking, $event.target.value)"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="canceled">Canceled</option>
                <option value="no_show">No show</option>
              </select>
            </div>
          </article>
        </div>
      </aside>
    </div>

    <transition name="fade">
      <div v-if="selectedBooking" class="modal-overlay" @click.self="closeModal">
        <div class="modal">
          <div class="modal-header">
            <div>
              <p class="eyebrow">Appointment details</p>
              <h2>{{ selectedBooking.name || 'Unnamed client' }}</h2>
            </div>
            <button class="icon-btn" @click="closeModal">×</button>
          </div>

          <dl class="details-grid">
            <div>
              <dt>Date</dt>
              <dd>{{ formatDate(selectedBooking.date) }} at {{ selectedBooking.time }}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd><span class="status" :class="selectedBooking.status">{{ statusLabel(selectedBooking.status) }}</span></dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{{ selectedBooking.phone || 'Not captured' }}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{{ selectedBooking.email || 'Not captured' }}</dd>
            </div>
            <div>
              <dt>Service</dt>
              <dd>{{ selectedBooking.service || 'Not captured' }}</dd>
            </div>
            <div>
              <dt>Source session</dt>
              <dd>{{ selectedBooking.session_id || 'No session id' }}</dd>
            </div>
          </dl>

          <div class="notes-box">
            <strong>Notes</strong>
            <p>{{ selectedBooking.notes || 'No notes captured by the agent.' }}</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import Cookies from 'js-cookie'
import { useRouter } from 'vue-router'
import * as api from '@/services/api.js'

const router = useRouter()

const bookings = ref([])
const agents = ref([])
const loading = ref(true)
const error = ref('')
const savingId = ref(null)
const selectedBooking = ref(null)
const currentDate = ref(new Date())

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const activeAgentId = computed(() => Cookies.get('active_agent_id'))

const activeAgentName = computed(() => {
  const agent = agents.value.find(item => String(item.id) === String(activeAgentId.value))
  return agent?.name || 'Active agent'
})

const upcomingBookings = computed(() => {
  return [...bookings.value]
    .filter(booking => booking.scheduledAt && booking.status !== 'canceled')
    .sort((a, b) => a.scheduledAt - b.scheduledAt)
})

const todayCount = computed(() => {
  const today = toDateKey(new Date())
  return bookings.value.filter(booking => booking.date === today).length
})

const weekCount = computed(() => {
  const now = new Date()
  const end = new Date(now)
  end.setDate(now.getDate() + 7)

  return bookings.value.filter(booking => {
    return booking.scheduledAt >= startOfDay(now) && booking.scheduledAt <= end
  }).length
})

const confirmedRate = computed(() => {
  if (!bookings.value.length) return 0
  const confirmed = bookings.value.filter(booking => booking.status === 'confirmed').length
  return Math.round((confirmed / bookings.value.length) * 100)
})

const currentMonthLabel = computed(() => {
  return currentDate.value.toLocaleDateString('en', {
    month: 'long',
    year: 'numeric'
  })
})

const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()
  const first = new Date(year, month, 1)
  const firstDay = (first.getDay() + 6) % 7
  const start = new Date(year, month, 1 - firstDay)
  const days = []

  for (let index = 0; index < 42; index += 1) {
    const date = new Date(start)
    date.setDate(start.getDate() + index)

    days.push({
      date: toDateKey(date),
      day: date.getDate(),
      inMonth: date.getMonth() === month,
      isToday: toDateKey(date) === toDateKey(new Date())
    })
  }

  return days
})

onMounted(loadPage)

async function loadPage() {
  loading.value = true
  error.value = ''

  try {
    const agentId = activeAgentId.value
    if (!agentId) {
      router.push('/agents')
      return
    }

    const [agentResponse, bookingResponse] = await Promise.all([
      api.agents.getAll(),
      api.booking.getBookingsByAgent(agentId)
    ])

    agents.value = normalizeList(agentResponse, ['agents', 'data'])
    bookings.value = normalizeList(bookingResponse, ['bookings', 'data']).map(normalizeBooking)
  } catch (err) {
    error.value = err.message || 'Unable to load booking data.'
    bookings.value = []
  } finally {
    loading.value = false
  }
}

function normalizeList(data, keys = []) {
  if (Array.isArray(data)) return data

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key]
  }

  return []
}

function normalizeBooking(row) {
  const scheduledAt = row.scheduled_at ? new Date(row.scheduled_at) : null

  return {
    ...row,
    scheduledAt,
    date: scheduledAt && !Number.isNaN(scheduledAt.getTime()) ? toDateKey(scheduledAt) : '',
    time: scheduledAt && !Number.isNaN(scheduledAt.getTime())
      ? scheduledAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : 'No time',
    status: row.status || 'confirmed',
    service: row.service || extractService(row.notes)
  }
}

function extractService(notes) {
  if (!notes) return ''
  const match = String(notes).match(/service:\s*([^,\n]+)/i)
  return match?.[1]?.trim() || ''
}

function getEvents(date) {
  return bookings.value.filter(booking => booking.date === date)
}

function selectDate(date) {
  const dayBookings = getEvents(date)
  if (dayBookings.length) openBooking(dayBookings[0])
}

function openBooking(booking) {
  selectedBooking.value = booking
}

function closeModal() {
  selectedBooking.value = null
}

async function updateStatus(booking, status) {
  const previousStatus = booking.status
  booking.status = status
  savingId.value = booking.id

  try {
    await api.booking.updateBooking(booking.id, { status })
  } catch (err) {
    booking.status = previousStatus
    error.value = err.message || 'Unable to update booking status.'
  } finally {
    savingId.value = null
  }
}

function prevMonth() {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() - 1,
    1
  )
}

function nextMonth() {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1,
    1
  )
}

function startOfDay(date) {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

function toDateKey(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDate(dateStr) {
  if (!dateStr) return 'No date'
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

function statusLabel(status) {
  return {
    pending: 'Pending',
    confirmed: 'Confirmed',
    canceled: 'Canceled',
    no_show: 'No show'
  }[status] || status
}
</script>

<style scoped>
.booking-page {
  min-height: 100vh;
  padding: 32px;
  background: #f6f7fb;
  color: #111827;
}

.page-header,
.header-actions,
.card-header,
.list-header,
.booking-item,
.booking-actions {
  display: flex;
}

.page-header {
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #0d8f62;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h1,
h2,
h3,
p {
  margin: 0;
}

h1 {
  font-size: clamp(2rem, 4vw, 3rem);
  letter-spacing: -0.04em;
}

.subtitle {
  max-width: 650px;
  margin-top: 8px;
  color: #66736b;
}

.header-actions {
  gap: 10px;
  flex-wrap: wrap;
}

.settings-btn,
.ghost-btn,
.icon-btn {
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 900;
  text-decoration: none;
}

.settings-btn {
  display: inline-flex;
  align-items: center;
  min-height: 42px;
  padding: 0 15px;
  background: #17211b;
  color: white;
}

.ghost-btn,
.icon-btn {
  background: white;
  color: #17211b;
  border: 1px solid #dfe7e1;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 14px;
}

.icon-btn {
  width: 38px;
  height: 38px;
  font-size: 1.3rem;
}

.kpi-grid,
.content-grid,
.loading-grid {
  display: grid;
  gap: 18px;
}

.kpi-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 18px;
}

.kpi-card,
.card,
.error-box,
.skeleton {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.05);
}

.kpi-card {
  padding: 18px;
}

.kpi-card span {
  color: #66736b;
  font-size: 13px;
  font-weight: 800;
}

.kpi-card strong {
  display: block;
  margin-top: 8px;
  font-size: 2rem;
  letter-spacing: -0.04em;
}

.content-grid,
.loading-grid {
  grid-template-columns: minmax(0, 1.5fr) minmax(360px, 0.8fr);
  align-items: start;
}

.card {
  padding: 20px;
}

.card-header,
.list-header {
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.list-header p {
  margin-top: 4px;
  color: #66736b;
  font-size: 14px;
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  border-radius: 999px;
  background: #edf7f1;
  color: #0d8f62;
  font-weight: 900;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 7px;
}

.day-name {
  color: #66736b;
  font-size: 12px;
  font-weight: 900;
  text-align: center;
}

.calendar-cell {
  min-height: 92px;
  padding: 8px;
  border: 1px solid #e7ece8;
  border-radius: 8px;
  background: #fbfdfb;
  color: #17211b;
  text-align: left;
  cursor: pointer;
}

.calendar-cell.muted {
  color: #a1aaa5;
  background: #f7f8fa;
}

.calendar-cell.today {
  border-color: #0d8f62;
  box-shadow: 0 0 0 3px rgba(13, 143, 98, 0.12);
}

.events {
  display: grid;
  gap: 4px;
  margin-top: 8px;
}

.event {
  overflow: hidden;
  border-radius: 999px;
  padding: 3px 7px;
  color: white;
  font-size: 11px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booking-list {
  display: grid;
  gap: 12px;
}

.booking-item {
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e7ece8;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
}

.booking-item p,
.booking-item small {
  display: block;
  margin-top: 5px;
  color: #66736b;
}

.booking-actions {
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

select {
  min-height: 34px;
  border: 1px solid #d7e0da;
  border-radius: 8px;
  background: white;
  color: #17211b;
  font: inherit;
  font-size: 13px;
}

.status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 900;
}

.pending,
.status.pending {
  background: #fef3c7;
  color: #92400e;
}

.confirmed,
.status.confirmed {
  background: #d1fae5;
  color: #047857;
}

.canceled,
.status.canceled {
  background: #fee2e2;
  color: #b91c1c;
}

.no_show,
.status.no_show {
  background: #e5e7eb;
  color: #374151;
}

.empty-state {
  padding: 34px 20px;
  border: 1px dashed #d7e0da;
  border-radius: 10px;
  color: #66736b;
  text-align: center;
}

.empty-state h3 {
  margin-bottom: 8px;
  color: #17211b;
}

.error-box {
  margin-bottom: 18px;
  padding: 14px 16px;
  color: #b91c1c;
  background: #fff7f7;
  border-color: #fecaca;
}

.error-box p {
  margin-top: 4px;
}

.skeleton {
  position: relative;
  overflow: hidden;
  min-height: 420px;
}

.skeleton.list {
  min-height: 360px;
}

.skeleton::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.75), transparent);
  animation: shimmer 1.2s infinite;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.42);
}

.modal {
  width: min(620px, 100%);
  border-radius: 14px;
  background: white;
  padding: 22px;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
}

.details-grid div,
.notes-box {
  padding: 12px;
  border-radius: 8px;
  background: #f7faf8;
}

dt {
  color: #66736b;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

dd {
  margin: 6px 0 0;
  color: #17211b;
  word-break: break-word;
}

.notes-box {
  margin-top: 12px;
}

.notes-box p {
  margin-top: 6px;
  color: #66736b;
}

.spin {
  display: inline-block;
  animation: spin 0.9s linear infinite;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1050px) {
  .content-grid,
  .loading-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .booking-page {
    padding: 18px 12px;
  }

  .page-header {
    flex-direction: column;
  }

  .header-actions,
  .settings-btn,
  .ghost-btn {
    width: 100%;
  }

  .settings-btn,
  .ghost-btn {
    justify-content: center;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .calendar-cell {
    min-height: 68px;
    padding: 5px;
  }

  .event {
    font-size: 10px;
    padding: 2px 5px;
  }

  .booking-item {
    flex-direction: column;
  }

  .booking-actions {
    justify-content: flex-start;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style>
